// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

// Plugins
import {
  convertTemperature,
  DASHBOARD_NULLISH_TEXT,
  formatNumber,
} from '../../../../../../plugins/general';
import {
  controlModeToTagType,
  controlModeToText,
  DEVICE_COMMUNICATION_THRESHOLD,
  enabledDisabledToText,
  getOperationalAlert,
  isEquipmentRunning,
  lubeTankHeaterStatusToTagType,
  runningStatusToTagType,
  runningStatusToText,
} from '../../../../../../plugins/crusher';
import { TEMPERATURE_RANGE } from '../../../../../../plugins/thresholds';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import {
  MeasurementType,
  TemperatureType,
} from '../../../../../../types/global';

// Components
import {
  AlertLine,
  // Play,
  // Stop
} from '../../../../../../layout/icons';
import { AlertType } from '../../../../../../layout/elements/Alert';
// import { Button, ButtonType } from '../../../../../../layout/elements/Button';
import { Col } from '../../../../../../layout/elements/Col';
import { Drawer, DrawerSize } from '../../../../../../layout/elements/Drawer';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
// import { PowerSwitch } from '../../../../../../components/PowerSwitch';
import { Row } from '../../../../../../layout/elements/Row';
// import { SwitchWithText } from '../../../../../../layout/elements/SwitchWithText';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Thermometer } from '../../../../../../layout/elements/Thermometer';

// Component
import { MotorControlProps } from './MotorControl.types';
import {
  BorderBottom,
  ColumnBorderRight,
  ControlModeContainer,
  LubeTankHeaterStatusContainer,
  // ControllerModuleContainer,
  // Controllers,
  // CrusherMotorContainer,
  // DischargeContainer,
  // ExecuteCommandContainer,
  // FeederContainer,
  // LubeTankHeaterContainer,
  // LubeUnitContainer,
  MonitoringContainer,
  MonitoringStatus,
  MonitoringStatusContainer,
  MonitoringStatusContent,
  // MotorControlContainer,
  // StatusContainer,
  // StopStartButtonsContainer,
  ThermometerContainer,
} from './MotorControl.style';

function MotorControl({
  className,
  isDrawerOpen,
  onCloseDrawer,
  unitData,
  edgeStatus,
  testId,
}: MotorControlProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();

  const userTemperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);

  const alertText: string | null = getOperationalAlert(
    edgeStatus?.edgeStatus,
    unitData?.timestamp,
    unitData?.readStatus,
    DEVICE_COMMUNICATION_THRESHOLD,
  );

  /**
   * Function to format the running hours with the correct pluralization.
   *
   * @param {number | null | undefined} hours - The number of running hours
   * @returns {string} - Returns the formatted hours string or nullish text if hours is
   * null/undefined
   */
  const formatRunningHours = (hours: number | null | undefined): string => {
    let formattedHours: string = DASHBOARD_NULLISH_TEXT;

    if (!_.isNil(hours)) {
      formattedHours = hours === 1
        ? t('dates.hour.singular')
        : t('dates.hour.plural', { hours: formatNumber(hours) });
    }

    return formattedHours;
  };

  return (
    <Drawer
      testId={testId}
      className={className}
      title={t('view.crusher.motorControl.title')}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      alert={
        alertText
          ? {
            type: AlertType.warning,
            icon: <AlertLine />,
            description: alertText,
          }
          : false
      }
      footer={false}
      size={DrawerSize.lg}
    >
      <Row>
        <Col>
          <Paragraph weight={ParagraphWeight.bold}>
            {t('view.crusher.motorControl.drawer.monitoring')}
          </Paragraph>
        </Col>
      </Row>
      <MonitoringContainer>
        <BorderBottom>
          <Row>
            <Col>
              <ControlModeContainer data-testid={`${testId}-control-mode-container`}>
                <Paragraph
                  size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  weight={ParagraphWeight.medium}
                >
                  {t('view.crusher.motorControl.title')}
                </Paragraph>
                <Tag
                  text={controlModeToText(unitData?.motorControl)}
                  type={
                    controlModeToTagType(
                      unitData?.motorControl,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )
                  }
                />
              </ControlModeContainer>
            </Col>
          </Row>
        </BorderBottom>
        <Row>
          <ColumnBorderRight xs={24} xl={16} testId={`${testId}-column-border-right`}>
            <MonitoringStatusContainer>
              <MonitoringStatusContent data-testid={`${testId}-monitoring-status-content`}>
                <MonitoringStatus>
                  <Paragraph
                    weight={ParagraphWeight.medium}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  >
                    {t('view.crusher.motorControl.drawer.lubeUnitRunning')}
                  </Paragraph>
                  <Tag
                    text={runningStatusToText(unitData?.lubePumpRunningStatus)}
                    type={
                      runningStatusToTagType(
                        unitData?.lubePumpRunningStatus,
                        edgeStatus ? edgeStatus.edgeStatus : null,
                        unitData?.timestamp,
                      )
                    }
                  />
                </MonitoringStatus>
                <Paragraph
                  weight={ParagraphWeight.bold}
                  size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                >
                  {formatRunningHours(unitData?.lubePump1RunHours)}
                </Paragraph>
              </MonitoringStatusContent>
              {unitData?.dischargeSuperiorEnable && (
                <MonitoringStatusContent>
                  <MonitoringStatus>
                    <Paragraph
                      weight={ParagraphWeight.medium}
                      size={ParagraphSize.sm}
                    >
                      {t('view.crusher.motorControl.drawer.dischargeConveyorRunning')}
                    </Paragraph>
                    <Tag
                      text={runningStatusToText(unitData?.dischargeRunningStatus)}
                      type={
                        runningStatusToTagType(
                          unitData?.dischargeRunningStatus,
                          edgeStatus ? edgeStatus.edgeStatus : null,
                          unitData?.timestamp,
                        )
                      }
                    />
                  </MonitoringStatus>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                  >
                    {formatRunningHours(unitData?.dischargeRunHours)}
                  </Paragraph>
                </MonitoringStatusContent>
              )}
              <MonitoringStatusContent>
                <MonitoringStatus>
                  <Paragraph
                    weight={ParagraphWeight.medium}
                    size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  >
                    {t('view.crusher.motorControl.drawer.crusherMotorRunning')}
                  </Paragraph>
                  <Tag
                    text={runningStatusToText(unitData?.crusherMotor)}
                    type={
                    runningStatusToTagType(
                      unitData?.crusherMotor,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )
                  }
                  />
                </MonitoringStatus>
                <Paragraph
                  weight={ParagraphWeight.bold}
                  size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                >
                  {formatRunningHours(unitData?.crusherMotor1RunHours)}
                </Paragraph>
              </MonitoringStatusContent>
              {unitData?.feedSuperiorEnable && (
                <MonitoringStatusContent>
                  <MonitoringStatus>
                    <Paragraph
                      weight={ParagraphWeight.medium}
                      size={ParagraphSize.sm}
                    >
                      {t('view.crusher.motorControl.drawer.feederConveyorRunning')}
                    </Paragraph>
                    <Tag
                      text={runningStatusToText(unitData?.feederRunningStatus)}
                      type={
                          runningStatusToTagType(
                            unitData?.feederRunningStatus,
                            edgeStatus ? edgeStatus.edgeStatus : null,
                            unitData?.timestamp,
                          )
                        }
                    />
                  </MonitoringStatus>
                  <Paragraph
                    weight={ParagraphWeight.bold}
                    size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
                  >
                    {formatRunningHours(unitData?.feederRunHours)}
                  </Paragraph>
                </MonitoringStatusContent>
              )}
            </MonitoringStatusContainer>
          </ColumnBorderRight>
          <Col xs={24} xl={8}>
            <ThermometerContainer data-testid={`${testId}-thermometer-container`}>
              <Thermometer
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                thresholds={{
                  lowAlarm: convertTemperature(
                    unitData?.spLubeTankTempLowAlarm,
                    userTemperatureUnit,
                  ).value,
                  lowWarning: convertTemperature(
                    unitData?.spLubeTankTempLowWarning,
                    userTemperatureUnit,
                  ).value,
                  highWarning: convertTemperature(
                    unitData?.spLubeTankTempHighWarning,
                    userTemperatureUnit,
                  ).value,
                  highAlarm: convertTemperature(
                    unitData?.spLubeTankTempHighAlarm,
                    userTemperatureUnit,
                  ).value,
                }}
                minValue={_.isNil(unitData?.lubricationTankTemperature)
                  ? null : TEMPERATURE_RANGE(userTemperatureUnit).minValue}
                maxValue={_.isNil(unitData?.lubricationTankTemperature)
                  ? null : TEMPERATURE_RANGE(userTemperatureUnit).maxValue}
                value={convertTemperature(
                  unitData?.lubricationTankTemperature,
                  userTemperatureUnit,
                ).value}
                unit={userTemperatureUnit}
                title={`${t('view.crusher.motorControl.drawer.lubeTankTemp')}`}
              />
            </ThermometerContainer>
          </Col>
        </Row>
        <Row>
          <Col>
            <LubeTankHeaterStatusContainer
              data-testid={`${testId}-lube-tank-heater-status-container`}
            >
              <MonitoringStatus>
                <Paragraph
                  weight={ParagraphWeight.medium}
                  size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                >
                  {t('view.crusher.motorControl.drawer.lubeTankHeater')}
                </Paragraph>
                <Tag
                  text={enabledDisabledToText(unitData?.lubeTankHeaterEnable)}
                  type={
                    lubeTankHeaterStatusToTagType(
                      unitData?.lubeTankHeaterEnable,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )
                  }
                />
              </MonitoringStatus>
            </LubeTankHeaterStatusContainer>
          </Col>
        </Row>
      </MonitoringContainer>
      {/* @TODO: back this flow when add control */}
      {/* <Row> */}
      {/*  <Col> */}
      {/*    <Paragraph weight={ParagraphWeight.bold}> */}
      {/*      {t('view.crusher.motorControl.drawer.controllers')} */}
      {/*    </Paragraph> */}
      {/*  </Col> */}
      {/* </Row> */}
      {/* <ControllerModuleContainer> */}
      {/*  <BorderBottom> */}
      {/*    <Row> */}
      {/*      <Col cols={12}> */}
      {/*        <MotorControlContainer> */}
      {/*          <Paragraph weight={ParagraphWeight.bold}> */}
      {/*            {t('view.crusher.motorControl.title')} */}
      {/*          </Paragraph> */}
      {/*          <Controllers> */}
      {/*            <Paragraph */}
      {/*              size={ParagraphSize.sm} */}
      {/*              weight={ParagraphWeight.bold} */}
      {/*            > */}
      {/*              {t('view.crusher.motorControl.drawer.mode')} */}
      {/*            </Paragraph> */}
      {/*            <SwitchWithText */}
      {/*              leftText={`${t('view.crusher.motorControl.manual')}`} */}
      {/*              rightText={`${t('common.auto')}`} */}
      {/*            /> */}
      {/*          </Controllers> */}
      {/*          <StopStartButtonsContainer> */}
      {/*            <Button */}
      {/*              type={ButtonType.secondary} */}
      {/*              disabled */}
      {/*              onClick={() => alert('Auto stop button was clicked!')} */}
      {/*            > */}
      {/*              {t('view.crusher.motorControl.drawer.autoStop')} */}
      {/*            </Button> */}
      {/*            <Button */}
      {/*              type={ButtonType.secondary} */}
      {/*              disabled */}
      {/*              onClick={() => alert('Auto start button was clicked!')} */}
      {/*            > */}
      {/*              {t('view.crusher.motorControl.drawer.autoStart')} */}
      {/*            </Button> */}
      {/*          </StopStartButtonsContainer> */}
      {/*          <Button */}
      {/*            disabled */}
      {/*            onClick={() => alert('Execute command button was clicked!')} */}
      {/*          > */}
      {/*            {t('common.executeCommand')} */}
      {/*          </Button> */}
      {/*        </MotorControlContainer> */}
      {/*      </Col> */}
      {/*      <Col cols={12}> */}
      {/*        <LubeTankHeaterContainer> */}
      {/*          <Paragraph */}
      {/*            weight={ParagraphWeight.bold} */}
      {/*          > */}
      {/*            {t('view.crusher.motorControl.drawer.controllersLubeTankHeater')} */}
      {/*          </Paragraph> */}
      {/*          <Controllers> */}
      {/*            <Paragraph */}
      {/*              size={ParagraphSize.sm} */}
      {/*              weight={ParagraphWeight.bold} */}
      {/*            > */}
      {/*              {t('common.status')} */}
      {/*            </Paragraph> */}
      {/*            <PowerSwitch /> */}
      {/*          </Controllers> */}
      {/*          <ExecuteCommandContainer> */}
      {/*            <Button */}
      {/*              disabled */}
      {/*              onClick={() => alert('Bowl adjustment button was clicked!')} */}
      {/*            > */}
      {/*              {t('common.executeCommand')} */}
      {/*            </Button> */}
      {/*          </ExecuteCommandContainer> */}
      {/*        </LubeTankHeaterContainer> */}
      {/*      </Col> */}
      {/*    </Row> */}
      {/*  </BorderBottom> */}
      {/*  <BorderBottom> */}
      {/*    <Row> */}
      {/*      <Col cols={12}> */}
      {/*        <LubeUnitContainer> */}
      {/*          <StatusContainer> */}
      {/*            <Paragraph */}
      {/*              weight={ParagraphWeight.bold} */}
      {/*            > */}
      {/*              {t('view.crusher.motorControl.drawer.lubeUnit')} */}
      {/*            </Paragraph> */}
      {/*            <Tag */}
      {/*              text={runningStatusToText(data.lubePumpRunningStatus)} */}
      {/*              type={ */}
      {/*              runningStatusToTagType( */}
      {/*                data.lubePumpRunningStatus, */}
      {/*                edgeDeviceOperationalStatus, */}
      {/*              ) */}
      {/*            } */}
      {/*            /> */}
      {/*          </StatusContainer> */}
      {/*          <Controllers> */}
      {/*            <Paragraph */}
      {/*              size={ParagraphSize.sm} */}
      {/*              weight={ParagraphWeight.bold} */}
      {/*            > */}
      {/*              {t('view.crusher.motorControl.drawer.currentValue')} */}
      {/*            </Paragraph> */}
      {/*            <Paragraph */}
      {/*              size={ParagraphSize.sm} */}
      {/*              weight={ParagraphWeight.bold} */}
      {/*            > */}
      {/*              {data.lubePump1RunHours === 1 */}
      {/*                ? t('dates.hour.singular') */}
      {/*                : t('dates.hour.plural', { */}
      {/*                  hours: formatNumber(data.lubePump1RunHours), */}
      {/*                })} */}
      {/*            </Paragraph> */}
      {/*          </Controllers> */}
      {/*          <Button */}
      {/*            disabled */}
      {/*            onClick={() => alert('Start button was clicked!')} */}
      {/*          > */}
      {/*            <Play /> */}
      {/*            {t('common.start')} */}
      {/*          </Button> */}
      {/*        </LubeUnitContainer> */}
      {/*      </Col> */}
      {/*      <Col cols={12}> */}
      {/*        <DischargeContainer> */}
      {/*          <StatusContainer> */}
      {/*            <Paragraph weight={ParagraphWeight.bold}> */}
      {/*              {t('view.crusher.motorControl.drawer.dischargeConveyor')} */}
      {/*            </Paragraph> */}
      {/*            <Tag */}
      {/*              text={runningStatusToText(data.dischargeRunningStatus)} */}
      {/*              type={ */}
      {/*              runningStatusToTagType( */}
      {/*                data.dischargeRunningStatus, */}
      {/*                edgeDeviceOperationalStatus, */}
      {/*              ) */}
      {/*            } */}
      {/*            /> */}
      {/*          </StatusContainer> */}
      {/*          <Controllers> */}
      {/*            <Paragraph size={ParagraphSize.sm} weight={ParagraphWeight.bold}> */}
      {/*              {t('view.crusher.motorControl.drawer.currentValue')} */}
      {/*            </Paragraph> */}
      {/*            <Paragraph size={ParagraphSize.sm} weight={ParagraphWeight.bold}> */}
      {/*              {data.dischargeRunHours === 1 */}
      {/*                ? t('dates.hour.singular') */}
      {/*                : t('dates.hour.plural', { */}
      {/*                  hours: data.dischargeRunHours, */}
      {/*                })} */}
      {/*            </Paragraph> */}
      {/*          </Controllers> */}
      {/*          <Button disabled onClick={() => alert('Stop button was clicked!')}> */}
      {/*            <Stop /> */}
      {/*            {t('common.stop')} */}
      {/*          </Button> */}
      {/*        </DischargeContainer> */}
      {/*      </Col> */}
      {/*    </Row> */}
      {/*  </BorderBottom> */}
      {/*  <Row> */}
      {/*    <Col cols={12}> */}
      {/*      <CrusherMotorContainer> */}
      {/*        <StatusContainer> */}
      {/*          <Paragraph weight={ParagraphWeight.bold}> */}
      {/*            {t('view.crusher.motorControl.drawer.crusherMotor')} */}
      {/*          </Paragraph> */}
      {/*          <Tag */}
      {/*            text={runningStatusToText(data.crusherMotor)} */}
      {/*            type={ */}
      {/*            runningStatusToTagType( */}
      {/*              data.crusherMotor, */}
      {/*              edgeDeviceOperationalStatus, */}
      {/*            ) */}
      {/*          } */}
      {/*          /> */}
      {/*        </StatusContainer> */}
      {/*        <Controllers> */}
      {/*          <Paragraph */}
      {/*            size={ParagraphSize.sm} */}
      {/*            weight={ParagraphWeight.bold} */}
      {/*          > */}
      {/*            {t('view.crusher.motorControl.drawer.currentValue')} */}
      {/*          </Paragraph> */}
      {/*          <Paragraph */}
      {/*            size={ParagraphSize.sm} */}
      {/*            weight={ParagraphWeight.bold} */}
      {/*          > */}
      {/*            {data.crusherMotor1RunHours === 1 */}
      {/*              ? t('dates.hour.singular') */}
      {/*              : t('dates.hour.plural', { */}
      {/*                hours: formatNumber(data.crusherMotor1RunHours), */}
      {/*              })} */}
      {/*          </Paragraph> */}
      {/*        </Controllers> */}
      {/*        <Button */}
      {/*          disabled */}
      {/*          onClick={() => alert('Start button was clicked!')} */}
      {/*        > */}
      {/*          <Play /> */}
      {/*          {t('common.start')} */}
      {/*        </Button> */}
      {/*      </CrusherMotorContainer> */}
      {/*    </Col> */}
      {/*    <Col cols={12}> */}
      {/*      <FeederContainer> */}
      {/*        <StatusContainer> */}
      {/*          <Paragraph weight={ParagraphWeight.bold}> */}
      {/*            {t('view.crusher.motorControl.drawer.feederConveyor')} */}
      {/*          </Paragraph> */}
      {/*          <Tag */}
      {/*            text={runningStatusToText(data.feederRunningStatus)} */}
      {/*            type={ */}
      {/*            runningStatusToTagType( */}
      {/*              data.feederRunningStatus, */}
      {/*              edgeDeviceOperationalStatus, */}
      {/*            ) */}
      {/*          } */}
      {/*          /> */}
      {/*        </StatusContainer> */}
      {/*        <Controllers> */}
      {/*          <Paragraph */}
      {/*            size={ParagraphSize.sm} */}
      {/*            weight={ParagraphWeight.bold} */}
      {/*          > */}
      {/*            {t('view.crusher.motorControl.drawer.currentValue')} */}
      {/*          </Paragraph> */}
      {/*          <Paragraph */}
      {/*            size={ParagraphSize.sm} */}
      {/*            weight={ParagraphWeight.bold} */}
      {/*          > */}
      {/*            {data.feederRunHours === 1 */}
      {/*              ? t('dates.hour.singular') */}
      {/*              : t('dates.hour.plural', { */}
      {/*                hours: formatNumber(data.feederRunHours), */}
      {/*              })} */}
      {/*          </Paragraph> */}
      {/*        </Controllers> */}
      {/*        <Button */}
      {/*          disabled */}
      {/*          onClick={() => alert('Stop button was clicked!')} */}
      {/*        > */}
      {/*          <Stop /> */}
      {/*          {t('common.stop')} */}
      {/*        </Button> */}
      {/*      </FeederContainer> */}
      {/*    </Col> */}
      {/*  </Row> */}
      {/* </ControllerModuleContainer> */}
    </Drawer>
  );
}

export { MotorControl };
